import React, { ReactElement, useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import { MdQueue } from "react-icons/md";
import useKeyboardShortcuts from "../../../contexts/KeyboardShortcutContext";
import useSelectionState, { AssignmentType, Ticket } from "../../../pages/agent/selection-state";
import { NoDataOrError } from "../../../pages/error/Error";
import {
    ChatStatus, GetChatDocument, Queue, useAssignChatsMutation, User, useReopenChatsMutation, useTransferSettingsQuery,
} from "../../../__generated__/api";
import Avatar from "../../avatar/Avatar";
import Confirm from "../../confirm/Confirm";
import Loading from "../../loading/Loading";
import Select, { Option, OptionWithChildren, SelectInstanceHandler } from "../../select/Select";

enum Type {
    TRANSFER,
    ASSIGN,
    UNASSIGN,
    TAKE_OVER,
    REOPEN,
    UNIGNORE
}

const TITLE_TEXT = {
    [Type.TRANSFER]: "Transfer chat",
    [Type.ASSIGN]: "Assign chat",
    [Type.UNASSIGN]: "Unassign chat",
    [Type.TAKE_OVER]: "Take over chat",
    [Type.REOPEN]: "Reopen chat",
    [Type.UNIGNORE]: "Unignore chat",
};

const BUTTON_TEXT = {
    [Type.TRANSFER]: "Transfer chat",
    [Type.ASSIGN]: "Assign chat",
    [Type.UNASSIGN]: "Unassign chat",
    [Type.TAKE_OVER]: "Take over chat",
    [Type.REOPEN]: "Reopen chat",
    [Type.UNIGNORE]: "Unignore chat",

};

function isUser(user: User | Queue): user is User {
    return user.__typename === "User";
}
export default function Transfer(): ReactElement {
    const selectionState = useSelectionState();
    const [assignChat] = useAssignChatsMutation();
    const [reopenChat] = useReopenChatsMutation();

    const [shouldShow, setShouldShow] = useState<boolean>(false);
    const [type, setType] = useState<Type>(Type.TRANSFER);
    const canAddAssignee = (type === Type.ASSIGN || type === Type.TRANSFER);

    const { data, loading, error } = useTransferSettingsQuery({
        skip: !canAddAssignee,
    });

    const [message, setMessage] = useState<string>("");
    const [selectedAgent, setSelectedAgent] = useState<User | Queue>();
    const selectRef = React.createRef<SelectInstanceHandler>();
    const [selectedTicketIds, setSelectedTicketIds] = useState<string[]>([]);

    useEffect(() => {
        if (shouldShow) {
            selectRef.current?.focus();
        }
        setSelectedAgent(undefined);
        setMessage("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldShow]);

    const transfer = async () => {
        setShouldShow(false);

        const isTransfer = type === Type.TRANSFER;

        if (isTransfer) {
            // selectionState.delete(...selectedTicketIds);
            selectionState.closeCurrent();
        }
        if (type === Type.REOPEN || type === Type.UNIGNORE) {
            reopenChat({
                refetchQueries: isTransfer ? selectedTicketIds.map(id => ({
                    query: GetChatDocument,
                    variables: { id },
                })) : undefined,
                variables: {
                    ids: selectedTicketIds,
                },
            });
            selectionState.setChatStatuses(selectedTicketIds, ChatStatus.Assigned, AssignmentType.CURRENT_USER);
        } else {
            await assignChat({
                refetchQueries: isTransfer ? selectedTicketIds.map(id => ({
                    query: GetChatDocument,
                    variables: { id },
                })) : undefined,
                variables: {
                    ids: selectedTicketIds,
                    assignment: {
                        assigneeId: selectedAgent?.id,
                        shouldAssignSelf: type === Type.TAKE_OVER,
                        shouldUnassign: type === Type.UNASSIGN,
                        message,
                    },
                },
            });
            selectionState.setChatStatuses(
                selectedTicketIds,
                type === Type.UNASSIGN ? ChatStatus.ChatRequested : ChatStatus.Assigned,
                type === Type.TAKE_OVER ? AssignmentType.CURRENT_USER : AssignmentType.OTHER_USER,
            );
        }
    };

    const startAssign = () => {
        const selected = selectionState.getSelection().filter(row => row.status === ChatStatus.ChatRequested
            || row.status === ChatStatus.Connected);
        setSelectedTicketIds(selected.toIdList("id"));
        if (selected.isNotEmpty) {
            setType(Type.ASSIGN);
            setShouldShow(true);
        }
    };

    const startTransfer = (tickets?: Ticket[]) => {
        const selected = tickets ?? selectionState.getSelection().filter(row => row.status === ChatStatus.Assigned
            || row.status === ChatStatus.OnHold || row.status === ChatStatus.Snoozed);
        setSelectedTicketIds(selected.toIdList("id"));
        if (selected.isNotEmpty) {
            setType(Type.TRANSFER);
            setShouldShow(true);
        }
    };

    const startTakeOver = (tickets?: Ticket[]) => {
        const selected = tickets ?? selectionState.getSelection().filter(row => !row.isAssignedToCurrentUser);
        setSelectedTicketIds(selected.toIdList("id"));
        if (selected.isNotEmpty) {
            setType(Type.TAKE_OVER);
            setShouldShow(true);
        }
    };

    const startReopen = (tickets?: Ticket[]) => {
        const selected = tickets ?? selectionState.getSelection().filter(row => row.isAssignedToCurrentUser);
        setSelectedTicketIds(selected.toIdList("id"));
        if (selected.isNotEmpty) {
            setType(selected.firstElement?.status === ChatStatus.Ignored ? Type.UNIGNORE : Type.REOPEN);
            setShouldShow(true);
        }
    };

    const startAssignSelf = async (selected: Ticket[]) => {
        const ids = selected.toIdList("id");
        setSelectedTicketIds(ids);
        if (selected.isNotEmpty) {
            await assignChat({
                variables: {
                    ids,
                    assignment: {
                        shouldAssignSelf: true,
                    },
                },
            });
            selectionState.setChatStatuses(ids, ChatStatus.Assigned, AssignmentType.CURRENT_USER);
        }
    };

    const startUnassign = async (tickets?: Ticket[]) => {
        const selected = tickets ?? selectionState.getSelection().filter(row => row.isAssignedToCurrentUser);
        setSelectedTicketIds(selected.toIdList("id"));
        if (selected.isNotEmpty) {
            setType(Type.UNASSIGN);
            setShouldShow(true);
        }
    };

    useKeyboardShortcuts(shortcuts => {
        shortcuts.on({
            shortcut: "k+t",
            context: "default",
            description: "Transfers the conversation",
            group: "Conversations",
            handler: () => startTransfer(),
        });
        shortcuts.on({
            shortcut: "k+a",
            context: "default",
            description: "Assigns the conversation",
            group: "Conversations",
            handler: () => startAssign(),
        });
        shortcuts.on({
            shortcut: "k+u",
            context: "default",
            description: "Unassigns the conversation",
            group: "Conversations",
            handler: () => startUnassign(),
        });
    });

    selectionState.registerAssignHandler(startAssign);
    selectionState.registerTransferHandler(startTransfer);
    selectionState.registerTakeOverHandler(startTakeOver);
    selectionState.registerReopenHandler(startReopen);
    selectionState.registerUnassignHandler(startUnassign);

    selectionState.registerAssignSelfHandler(startAssignSelf);

    if (loading) {
        return <Loading />;
    }
    if ((!data && canAddAssignee) || error) {
        return <NoDataOrError error={error} />;
    }

    const agentName = (selectedAgent as User)?.firstName ? ` ${(selectedAgent as User).firstName}` : "";
    let placeholder = (!canAddAssignee || !agentName)
        ? "Let me help out with this one.."
        : `Hey${agentName}, can you take a look at this one?`;
    if (type === Type.UNASSIGN) {
        placeholder = "I need to put this back into the queue because...";
    }
    type AssigneeOptions = Option<Partial<User | Queue>>[] | OptionWithChildren<Partial<User | Queue>>[];
    let assigneeOptions: AssigneeOptions = data?.settings?.agents?.map(agent => ({
        label: (
            <>
                <Avatar src={agent.avatarUrl as string} variant="xs" />
                {agent.name}
            </>
        ),
        value: agent.id,
        data: agent,
    })) ?? [];
    if (data?.queues.isNotEmpty) {
        assigneeOptions = [
            {
                label: "Queues",
                options: data.queues.map(queue => ({
                    label: queue.name ?? "",
                    value: queue.id,
                    data: queue,
                })),
            },
            {
                label: "Assignees",
                options: assigneeOptions,
            },
        ] as OptionWithChildren<Partial<User | Queue>>[];
    }
    return (
        <Confirm
            confirmText={BUTTON_TEXT[type]}
            shouldShow={shouldShow}
            closeText="Close"
            onHide={() => setShouldShow(false)}
            onConfirm={() => transfer()}
        >
            <div className="flex-grow-1 p-3 d-flex flex-column">
                <h5 className="mb-4">
                    {TITLE_TEXT[type]}
                </h5>
                {canAddAssignee && (
                    <div className="mb-4">
                        <Form.Label className="text-primary">Assignee</Form.Label>
                        <Select
                            ref={selectRef}
                            placeholder="Assignee"
                            options={assigneeOptions}
                            filterOption={(user, input) => (input
                                ? user.name?.toLowerCase().includes(input.toLowerCase())
                                : true) ?? true}
                            onChange={selected => setSelectedAgent(selected.data as User)}
                            value={selectedAgent ? {
                                label: !isUser(selectedAgent) ? selectedAgent.name : (
                                    <>
                                        <Avatar src={selectedAgent.avatarUrl as string} variant="xs" />
                                        {selectedAgent.name}
                                    </>
                                ),
                                value: selectedAgent.id,
                                data: selectedAgent,
                            } : undefined}
                        />
                        <Form.Text>
                            Any additional comments or context you want to leave with the new assignee
                        </Form.Text>

                    </div>
                )}

                <Form.Label className="text-primary">
                    {canAddAssignee && "Assignee"}
                    {" "}
                    Comments
                </Form.Label>
                <Form.Control
                    as="textarea"
                    type="text"
                    placeholder={placeholder}
                    className="default-text-height p-2"
                    onChange={evt => setMessage(evt.target.value)}
                />
                <Form.Text>
                    {!canAddAssignee
                        ? "Any additional comments you want to leave on the ticket"
                        : "Any additional comments or context you want to leave for the new assignee"}

                </Form.Text>

            </div>
        </Confirm>
    );
}

Transfer.defaultProps = {
    refreshData: () => {
        // nothing
    },
};
